import { ReactElement, useMemo } from 'react';
import {
  CustomAttributesEntity,
  getCustomAttributesConfig,
} from 'src/graphql/entities/custom-attributes/custom-attributes-config/custom-attributes-config';
import { useCustomAttributesSidePanelData } from 'src/graphql/entities/custom-attributes/hooks/use-custom-attributes-side-panel-data';
import {
  SidePanelPropertyKey,
  SidePanelSectionKey,
  useCardCustomAttributeKeys,
  useSectionVisibility,
} from 'src/graphql/entities/side-panel-personalization';
import { CustomAttributeRow } from './custom-attribute-row';

/** A card's visible attribute rows by property key, in layout order. */
export type CardCustomAttributeRows = Readonly<
  Partial<Record<SidePanelPropertyKey, ReactElement>>
>;

interface UseCardCustomAttributeRowsParams {
  sectionKey: SidePanelSectionKey;
  entity: CustomAttributesEntity;
  entityId: string;
  partnerId: number | undefined;
  disabled: boolean;
}

const NO_ROWS: CardCustomAttributeRows = {};

/** The values come from the list's `useLoadCardCustomAttributeValues`; the card only reads the cache. */
export function useCardCustomAttributeRows({
  sectionKey,
  entity,
  entityId,
  partnerId,
  disabled,
}: UseCardCustomAttributeRowsParams): CardCustomAttributeRows {
  const keys = useCardCustomAttributeKeys(sectionKey);
  const isVisible = useSectionVisibility(sectionKey);
  const { useCachedStoredAttributes } = getCustomAttributesConfig(entity);
  const stored = useCachedStoredAttributes(entityId);

  const {
    rowsByPropertyKey,
    loading,
    payloadError,
    onSave,
    disabled: rowsDisabled,
  } = useCustomAttributesSidePanelData({
    entity,
    entityId,
    partnerId: keys.length > 0 ? partnerId : undefined,
    canEdit: !disabled,
    stored,
  });

  return useMemo(() => {
    // until the values land the rows hold definition defaults — never show those as the entity's
    if (loading || payloadError) {
      return NO_ROWS;
    }

    return Object.fromEntries(
      keys.flatMap((key) => {
        const row = rowsByPropertyKey.get(key);

        return row && isVisible(key, row.isEmpty)
          ? [
              [
                key,
                <CustomAttributeRow
                  key={key}
                  row={row}
                  onSave={onSave}
                  disabled={rowsDisabled}
                />,
              ],
            ]
          : [];
      }),
    );
  }, [keys, isVisible, rowsByPropertyKey, loading, payloadError, onSave, rowsDisabled]);
}
